import clsx from 'clsx'

import { FvButton, FvLinkButton, TextAreaField } from '@fv/client-components'
import { addressFuncs, useFormModel, validateEmailList } from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'
import type { LocationAccessorialType, Workflow } from '@fv/models'

import { AddressInput } from '@/components/inputs/AddressInput'
import { ContactPhoneInput } from '@/components/inputs/ContactPhoneInput'
import {
  InputAdornment,
  InputGroup,
  InputGroupWrapper,
} from '@/components/inputs/InputGroup'
import { FtlLocationAccessorials } from '@/components/inputs/LocationAccessorials'
import { ShipTypeSelector } from '@/components/inputs/ShipTypeSelector'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { getMapUrl } from '@/utils/getMapUrl'

import { AddressBookButton } from '../addresses/AddressBookButton'
import { HideForVendor } from '../auth/HasRole'
import { useRole } from '../auth/hooks'
import { ShareShipmentWith } from '../book/ShareShipmentWith'
import { loadLocationContactPhoneRequired } from '../book-eligibility/bookEligibility'
import { useAccountSettings } from '../settings/account-settings/hooks'
import {
  type BaseShipmentFormSectionProps,
  pickFormSectionProps,
  ShipmentFormSection,
} from '../shipment-form/ShipmentFormSection'
import { DockTimeInput } from './DockTimeInput'
import { LocationAccessorialForm } from './LocationAccessorialForm'
import {
  type PillDateProps,
  ShipmentLocationPillHeader,
} from './ShipmentLocationPillHeader'
import {
  mapAddressBookToShipmentLocationFormModel,
  mapAtlasAddressToShipmentLocationFormModel,
  type ShipmentLocationFormModel,
} from './types'

type Props = BaseShipmentFormSectionProps &
  PillDateProps & {
    value: ShipmentLocationFormModel
    disabled?: boolean
    isTemplate?: boolean
    workflow: Workflow
    onChange: (value: ShipmentLocationFormModel) => void
    requireEmail?: boolean
    onSchedulePickupChange?: (schedule: boolean) => void
    schedulePickup?: boolean
    pillClassName?: string
    pillClickable?: boolean
    onAddStop?: () => void
    onRemoveStop?: () => void
  }
export const ShipmentLocationForm = (props: Props) => {
  const {
    workflow,
    value,
    disabled,
    onChange,
    isTemplate = false,
    requireEmail,
    allowDateEdit,
    onSchedulePickupChange,
    schedulePickup,
    onAddStop,
    onRemoveStop,
  } = props
  const role = useRole()
  const accountSettings = useAccountSettings()
  const { register } = useFormModel({
    value,
    onChange,
  })

  const isFreight = workflow === 'ltl' || workflow === 'truckload'
  const isOrigin = value.type === 'origin'
  const stopTypeDisplay = isOrigin ? 'pickup' : 'delivery'

  const locAccessorials =
    value?.accessorials.map<LocationAccessorialType>(a => a.key) ?? []
  const contactPhoneRequired =
    !isTemplate &&
    loadLocationContactPhoneRequired({
      type: value.type,
      ...(value.shipType ? { shipType: value.shipType } : {}),
      accessorials: locAccessorials,
    })
  const fieldsRequired = !isTemplate
  const emailRequired = !isTemplate && requireEmail
  const dockTimeRequired = !isTemplate && isOrigin
  const refNumRequired =
    !isTemplate &&
    (isOrigin
      ? accountSettings.booking?.shipperNumberRequired
      : accountSettings.booking?.poNumberRequired)

  if (!location) return null

  function handleAddressSelect(address: ClientAddressLookupResult | null) {
    onChange({
      ...value,
      ...mapAddressBookToShipmentLocationFormModel(address, workflow, false),
    })
  }
  function handleAtlasSelect(atlasAddress: ClientAddressLookupResult | null) {
    onChange({
      ...value,
      ...mapAtlasAddressToShipmentLocationFormModel(
        atlasAddress,
        workflow,
        false,
      ),
    })
  }

  const prefixName = (name: string) =>
    value.type === 'stop'
      ? `stop-${value.sequence}-${name}`
      : `${value.type}-${name}`

  return (
    <ShipmentFormSection
      className={clsx('bg-fv-gray-100', {
        'border-fv-green bg-fv-green-50': !isOrigin,
        'border-fv-orange bg-fv-orange-25': isOrigin,
      })}
      icon="map-marker"
      {...pickFormSectionProps(props)}
      pillClassName={clsx(
        {
          'border-fv-green bg-fv-green-50 border-2': !isOrigin,
          'border-fv-orange bg-fv-orange-25 border-2': isOrigin,
        },
        props.pillClassName,
      )}
      pillClickable={props.pillClickable}
      pillContents={
        <ShipmentLocationPillHeader
          value={value}
          workflow={workflow}
          onChange={onChange}
          allowDateEdit={allowDateEdit}
          {...(props.allowDateEdit && {
            allLocations: props.allLocations,
          })}
        />
      }
      title={isOrigin ? 'Pick up' : 'Deliver'}
    >
      {(onAddStop || onRemoveStop) && (
        <div className="absolute right-4 -top-4 flex gap-x-1">
          {onAddStop && (
            <ShipperTooltip label="Add a stop after this location.">
              <FvButton
                icon="plus"
                theme="round"
                disabled={disabled}
                onClick={onAddStop}
              />
            </ShipperTooltip>
          )}
          {onRemoveStop && (
            <ShipperTooltip label="Remove this stop.">
              <FvButton
                icon="trash"
                theme="round"
                iconClass="text-fv-orange"
                disabled={disabled}
                onClick={onRemoveStop}
              />
            </ShipperTooltip>
          )}
        </div>
      )}
      <div className="grid gap-x-2 grid-cols-[repeat(16,minmax(0,1fr))] @container">
        <InputGroupWrapper
          className="col-span-6 @cb1300:col-span-3"
          label="Company"
          name={prefixName('company')}
          required={fieldsRequired}
        >
          <AddressBookButton
            config={{ context: workflow, type: 'shipping' }}
            disabled={disabled}
            setAddress={handleAddressSelect}
          />

          <AddressInput
            context={workflow}
            allowCustomInput
            formatAddress={x => x.company ?? 'NA'}
            formatOptionLabel={addressFuncs.companyAddress}
            includeAddressBook
            includeAtlas={false}
            isClearable={false}
            location={value}
            name={prefixName('company')}
            onInputChange={company => onChange({ ...value, company })}
            onLocationChange={handleAddressSelect}
            placeholder="Company name"
            required={fieldsRequired}
          />
        </InputGroupWrapper>

        <InputGroup
          className="col-span-6 @cb1300:col-span-4"
          inputProps={{ ...register('address'), name: prefixName('address') }}
          inputType="text"
          label="Street address"
          required={fieldsRequired}
          startContent={
            <InputAdornment position="start">
              <FvLinkButton
                disabled={!value.postalCode}
                href={getMapUrl(value)}
                className={clsx(
                  isOrigin ? '!bg-fv-orange-50' : '!bg-fv-green-50',
                )}
                icon="map-marker"
                iconClass={clsx({
                  'text-fv-orange': isOrigin,
                  'text-fv-green': !isOrigin,
                })}
                rel="noreferrer"
                target="_blank"
                title="View in Google Maps"
              />
            </InputAdornment>
          }
        />

        <InputGroup
          className="col-span-4 @cb1300:col-span-2"
          inputProps={{
            ...register('address2'),
            name: prefixName('address2'),
            placeholder: 'Address 2',
          }}
          inputType="text"
          label="Address 2"
        />

        <InputGroupWrapper
          className="col-span-6 @cb1300:col-span-3"
          label="City, state & zip code"
          name={prefixName('cityStateZip')}
          required={fieldsRequired}
        >
          <AddressInput
            formatAddress={addressFuncs.cityStateZip}
            isClearable={false}
            location={value}
            name={prefixName('cityStateZip')}
            onLocationChange={handleAtlasSelect}
            placeholder="Zip/postal code"
            required={fieldsRequired}
          />
        </InputGroupWrapper>

        {isFreight && (
          <ShipTypeSelector
            name={prefixName('shipType')}
            value={{
              shipType: value.shipType ?? 'business dock',
              accessorials: value.accessorials ?? [],
            }}
            onChange={({ shipType, accessorials }) => {
              onChange({
                ...value,
                shipType: shipType === '' ? 'business dock' : shipType,
                accessorials,
              })
            }}
            showDetails
            showLabels
            className="col-span-6 @cb1300:col-span-4"
          />
        )}

        <InputGroup
          className="col-span-4 @cb1300:col-span-3"
          inputProps={{
            ...register('contactName'),
            name: prefixName('contactName'),
          }}
          inputType="text"
          label="Contact name"
          required={fieldsRequired}
        />

        <ContactPhoneInput
          className="col-span-5 @cb1300:col-span-3"
          disabled={disabled}
          name={prefixName('contactPhone')}
          onChange={values => onChange({ ...value, ...values })}
          required={contactPhoneRequired}
          values={value}
        />

        <InputGroup
          className="col-span-5 @cb1300:col-span-3"
          inputProps={{
            ...register('contactEmail', {
              validate: e =>
                validateEmailList(e)
                  ? ''
                  : 'please enter a comma separated list of emails',
            }),
            name: prefixName('contactEmail'),
          }}
          inputType="text"
          required={emailRequired}
          label="Contact email"
        />

        {(workflow === 'ltl' || isOrigin) && (
          <>
            <DockTimeInput
              className="col-span-3 @cb1300:col-span-2"
              label={isOrigin ? 'Ready time' : 'Open time'}
              value={value}
              name="opensAt"
              onChange={values => onChange({ ...value, ...values })}
              required={dockTimeRequired}
            />

            <DockTimeInput
              className="col-span-3 @cb1300:col-span-2"
              label="Close time"
              value={value}
              name="closesAt"
              onChange={values => onChange({ ...value, ...values })}
              required={dockTimeRequired}
            />
          </>
        )}

        {!isTemplate && (
          <InputGroup
            className="col-span-full @cb1300:col-span-3"
            inputProps={{ ...register('refNum'), name: prefixName('refNum') }}
            inputType="text"
            required={refNumRequired}
            label={
              role === 'vendor'
                ? 'Reference #'
                : isOrigin
                  ? "Shipper's #"
                  : 'Purchase order #'
            }
          />
        )}
      </div>

      <div className="w-full">
        <LocationAccessorialForm
          value={value}
          workflow={workflow}
          onChange={update => onChange({ ...value, ...update })}
        />
        {workflow === 'truckload' && (
          <FtlLocationAccessorials
            vertical
            className="flex gap-y-4 flex-col"
            accessorials={value.accessorials}
            inputNamePrefix={prefixName('locaccessorial')}
            openDatePicker={() => {
              /* no-op for truckload */
            }}
            onChange={accessorials => onChange({ ...value, accessorials })}
          />
        )}
        {onSchedulePickupChange && isOrigin && (
          <InputGroup
            className="col-span-4 mb-6"
            inputProps={{
              name: 'schedulePickup',
              onChange: e => onSchedulePickupChange(e.target.value === 'true'),
              value: String(schedulePickup ?? false),
              options: [
                { label: 'Daily pickup', value: 'false' },
                {
                  label: 'Schedule pickup',
                  value: 'true',
                },
              ],
            }}
            inputType="radio"
            label="Pickup type"
          />
        )}
      </div>
      <HideForVendor>
        <ShareShipmentWith
          className="my-3"
          location={value}
          onChange={({ copyBOL, shares }) =>
            onChange({ ...value, copyBOL, shares })
          }
        />
      </HideForVendor>

      <TextAreaField
        name={prefixName('instructions')}
        value={value.instructions}
        onChange={e => onChange({ ...value, instructions: e.target.value })}
        rows={3}
        placeholder={`Other ${stopTypeDisplay} instructions`}
        className="form-control h-auto mt-2"
      />
    </ShipmentFormSection>
  )
}
